import {useCallback, useEffect, useState} from "react";
import {get, post} from "../../utils/http.client";
import {apiBindings} from "../../utils/constants";
import {useApp} from "../../app.context";
import {useAccess} from "../../utils/useAccess";
import {EDITOR_ROLES, EDITOR_ROLE_LABELS, EditorRole} from "../../models/accessLevel.model";

type GameEnv = 'debug' | 'release';
type Mode = 'users' | 'games';

interface UserRow {
    id: string;
    displayName: string;
    email?: string;
    rating: number;
    wins: number;
    // Derived, not served: there is no Losses stat anywhere server-side. Mirrors
    // the Unity client's own derivation (ProfileList.cs: matches - wins).
    losses: number;
    matches: number;
    lastOnline?: string;
    // "OK" | "NO PROFILE" (PlayFab-only) | "NO PLAYFAB" (profile-only, unroleable).
    status?: string;
    // Editor role string ('admin', 'sysadmin', …) or undefined if none.
    editorRole?: string;
}

interface GameRow {
    sessionId: string;
    name: string;
    status: string;
    turn: number;
    players: string;
    created?: string;
    orphaned: boolean;
}

interface GameRef {
    sessionId: string;
    status: string;
    turn: number;
}

interface UserDetail {
    id: string;
    displayName?: string;
    email?: string;
    playFabAccount: boolean;
    editorRole?: string;
    rating: number;
    wins: number;
    matches: number;
    games: GameRef[];
    friends: string[];
    thumbnails: number;
}

// Backend POCOs serialize with PascalCase keys; read either casing defensively
// so the tab is robust to the serializer config.
const pick = (o: any, name: string) => o?.[name] ?? o?.[name.charAt(0).toLowerCase() + name.slice(1)];

function asUser(o: any): UserRow {
    const wins = pick(o, 'Wins') ?? 0;
    const matches = pick(o, 'Matches') ?? 0;
    return {
        id: pick(o, 'Id'),
        displayName: pick(o, 'DisplayName') ?? '(none)',
        email: pick(o, 'Email'),
        rating: pick(o, 'Rating') ?? 0,
        wins,
        losses: losses(wins, matches),
        matches,
        lastOnline: pick(o, 'LastOnline'),
        status: pick(o, 'Status'),
        editorRole: pick(o, 'EditorRole'),
    };
}

// Clamped so a stats blob where wins somehow exceeds matches shows 0 rather than
// a negative record.
function losses(wins: number, matches: number): number {
    return Math.max(0, matches - wins);
}

function asGame(o: any): GameRow {
    return {
        sessionId: pick(o, 'SessionId'),
        name: pick(o, 'Name') ?? '(unnamed)',
        status: pick(o, 'Status') ?? '',
        turn: pick(o, 'Turn') ?? 0,
        players: pick(o, 'Players') ?? '',
        created: pick(o, 'Created'),
        orphaned: pick(o, 'Orphaned') ?? false,
    };
}

function asDetail(o: any): UserDetail {
    return {
        id: pick(o, 'Id'),
        displayName: pick(o, 'DisplayName'),
        email: pick(o, 'Email'),
        playFabAccount: pick(o, 'PlayFabAccount') ?? false,
        editorRole: pick(o, 'EditorRole'),
        rating: pick(o, 'Rating') ?? 0,
        wins: pick(o, 'Wins') ?? 0,
        matches: pick(o, 'Matches') ?? 0,
        games: (pick(o, 'Games') ?? []).map((g: any) => ({
            sessionId: pick(g, 'SessionId'),
            status: pick(g, 'Status'),
            turn: pick(g, 'Turn') ?? 0,
        })),
        friends: pick(o, 'Friends') ?? [],
        thumbnails: pick(o, 'Thumbnails') ?? 0,
    };
}

function UsersEditorView() {
    const {setNotification} = useApp();
    const {canEditRoles} = useAccess();
    const [env, setEnv] = useState<GameEnv>('debug');
    const [mode, setMode] = useState<Mode>('users');
    // The input updates `filter` immediately (responsive typing); `debouncedFilter`
    // catches up 400ms after the last keystroke and is what actually filters, so a
    // large in-memory list isn't re-scanned on every keypress.
    const [filter, setFilter] = useState('');
    const [debouncedFilter, setDebouncedFilter] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedFilter(filter), 400);
        return () => clearTimeout(t);
    }, [filter]);
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(0);
    const [users, setUsers] = useState<UserRow[]>([]);
    const [games, setGames] = useState<GameRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    // Set when the backend served a list it knows to be incomplete (the PlayFab
    // enumeration failed or was truncated). The rows shown are still real — they
    // just aren't all of them — so this is a banner, not an error state.
    const [warning, setWarning] = useState<string | undefined>(undefined);
    const [selected, setSelected] = useState<UserRow | undefined>(undefined);
    const [detail, setDetail] = useState<UserDetail | undefined>(undefined);
    const [detailLoading, setDetailLoading] = useState(false);
    const [role, setRole] = useState<string>('none');
    const [applying, setApplying] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(undefined);
        setWarning(undefined);
        setSelected(undefined);
        setDetail(undefined);
        const [u, g] = await Promise.all([
            get(`${apiBindings.api}users?env=${env}`, undefined),
            get(`${apiBindings.api}games?env=${env}`, undefined),
        ]);
        // `users` returns {users, warning}; a bare array is the older shape, still
        // accepted so a frontend deploy doesn't have to be lockstep with the backend.
        const userRows = Array.isArray(u.data) ? u.data : pick(u.data, 'Users');
        setWarning(pick(u.data, 'Warning') || undefined);
        if (!u.success || !g.success || !Array.isArray(userRows) || !Array.isArray(g.data)) {
            // A 401 is an auth problem, not a config problem — most commonly
            // Safari discarding the cross-site session cookie (ITP blocks
            // third-party cookies), so the login "works" but no request
            // after it carries the session.
            const unauthorized = u.status === 401 || g.status === 401;
            setError(unauthorized
                ? 'Not authorized (401): your login session isn\'t reaching the server. ' +
                  'Safari blocks cross-site session cookies — use Chrome/Edge until bearer-token auth lands.'
                : u.data?.message || g.data?.message ||
                  'Failed to load. Confirm GAME_STORAGE/GAME_COSMOS settings are configured for this environment.');
            setUsers([]);
            setGames([]);
        } else {
            setUsers(userRows.map(asUser));
            setGames(g.data.map(asGame));
        }
        setLoading(false);
    }, [env]);

    useEffect(() => {
        load();
    }, [load]);

    // Reset to the first page when the active list, filter, or page size changes.
    useEffect(() => {
        setPage(0);
    }, [mode, debouncedFilter, pageSize, env]);

    async function openUser(u: UserRow) {
        setSelected(u);
        setDetail(undefined);
        setDetailLoading(true);
        const r = await get(`${apiBindings.api}userDetail?env=${env}&id=${encodeURIComponent(u.id)}`, undefined);
        setDetail(r.success && r.data ? asDetail(r.data) : undefined);
        setDetailLoading(false);
    }

    // Keep the role dropdown in sync with the loaded user.
    useEffect(() => {
        setRole(detail?.editorRole || 'none');
    }, [detail]);

    async function applyRole() {
        if (!selected) return;
        setApplying(true);
        const r = await post(`${apiBindings.api}setUserRole?env=${env}&id=${encodeURIComponent(selected.id)}&role=${role}`, '', undefined);
        setApplying(false);
        if (r.success) {
            const newRole = role === 'none' ? undefined : role;
            setDetail(d => d ? {...d, editorRole: newRole} : d);
            // Keep the list column in sync without a full reload.
            setUsers(us => us.map(u => u.id === selected.id ? {...u, editorRole: newRole} : u));
            setNotification({title: 'Role Updated', message: `${selected.displayName}: editor role set to ${role}.`});
        } else {
            setNotification({title: 'Update Failed', message: r.data?.message || 'Could not update the role.'});
        }
    }

    const f = debouncedFilter.trim().toLowerCase();
    // The PlayFab id has no column of its own any more, but stays searchable —
    // pasting an id from a log or the detail popup should still find its user.
    const filteredUsers = f
        ? users.filter(u => u.displayName.toLowerCase().includes(f)
            || (u.id ?? '').toLowerCase().includes(f)
            || (u.email ?? '').toLowerCase().includes(f))
        : users;
    const filteredGames = f
        ? games.filter(g => g.name.toLowerCase().includes(f) || g.players.toLowerCase().includes(f) || (g.sessionId ?? '').toLowerCase().includes(f))
        : games;

    const total = mode === 'users' ? filteredUsers.length : filteredGames.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.max(0, Math.min(page, totalPages - 1));
    const start = safePage * pageSize;
    const pageUsers = filteredUsers.slice(start, start + pageSize);
    const pageGames = filteredGames.slice(start, start + pageSize);

    const muted = {color: 'var(--muted-text-color)'};
    const mono = {fontFamily: 'monospace', fontSize: 12, ...muted};
    // Emails routinely outrun their column, so clip rather than let the cell push
    // the grid wide; the full value is on the cell's title tooltip.
    const emailCell = {...muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'} as const;
    const headerCell = {color: 'var(--muted-text-color)', fontSize: 12, fontWeight: 600};
    const usersGrid = '1.4fr 0.8fr 1.8fr 0.6fr 0.6fr 0.6fr 0.7fr 1.2fr';
    const gamesGrid = '1.6fr 1fr 0.5fr 2fr 1.2fr';

    return (
        <div className={'layout vertical box flex-grow'} style={{gap: 12, minHeight: 0}}>
            {/* Controls */}
            <div className={'layout horizontal center gap'} style={{flexWrap: 'wrap'}}>
                <p className={'text header'} style={{marginRight: 8}}>Users</p>

                <div className={'layout horizontal gap'}>
                    <button onClick={() => setEnv('debug')}
                            style={{opacity: env === 'debug' ? 1 : 0.5}}>debug</button>
                    <button onClick={() => setEnv('release')}
                            style={{opacity: env === 'release' ? 1 : 0.5}}>release</button>
                </div>

                <div className={'layout horizontal gap'} style={{marginLeft: 8}}>
                    <button onClick={() => setMode('users')}
                            style={{opacity: mode === 'users' ? 1 : 0.5}}>Users ({users.length})</button>
                    <button onClick={() => setMode('games')}
                            style={{opacity: mode === 'games' ? 1 : 0.5}}>Games ({games.length})</button>
                </div>

                <input style={{flexGrow: 1, minWidth: 160}} placeholder={'Filter...'}
                       value={filter} onChange={e => setFilter(e.target.value)}/>

                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} title={'Items per page'}>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                </select>

                <button onClick={load} title={'Reload'}><span className={'fa-solid fa-rotate-right'}/></button>
            </div>

            {env === 'release' &&
                <p className={'warning-text'} style={{fontSize: 13}}>release = staging/live data — read carefully.</p>}

            {error && <p className={'warning-text'}>{error}</p>}
            {!error && warning &&
                <p className={'warning-text'} style={{fontSize: 13}}>
                    <span className={'fa-solid fa-triangle-exclamation'} style={{marginRight: 6}}/>
                    Incomplete list — accounts with no game profile may be missing. {warning}
                </p>}
            {loading && <p style={muted}>Loading…</p>}

            {/* Table */}
            {!loading && !error && mode === 'users' &&
                <div className={'layout vertical'} style={{minHeight: 0, flexGrow: 1, overflowY: 'auto', gap: 2}}>
                    <div style={{display: 'grid', gridTemplateColumns: usersGrid, gap: 8, padding: '4px 8px'}}>
                        <span style={headerCell}>Display Name</span>
                        <span style={headerCell}>Role</span>
                        <span style={headerCell}>Email</span>
                        <span style={headerCell}>Rating</span>
                        <span style={headerCell}>Wins</span>
                        <span style={headerCell}>Losses</span>
                        <span style={headerCell}>Matches</span>
                        <span style={headerCell}>Last Online</span>
                    </div>
                    {pageUsers.map(u => (
                        <div key={u.id} onClick={() => openUser(u)}
                             className={'box'}
                             style={{display: 'grid', gridTemplateColumns: usersGrid, gap: 8, padding: '6px 8px', cursor: 'pointer', alignItems: 'center', color: 'white'}}>
                            <span>{u.displayName}{statusTag(u.status)}</span>
                            <span>{roleCell(u.editorRole)}</span>
                            <span style={emailCell} title={u.email}>{u.email || '—'}</span>
                            <span>{u.rating}</span>
                            <span>{u.wins}</span>
                            <span>{u.losses}</span>
                            <span>{u.matches}</span>
                            <span style={muted}>{formatDate(u.lastOnline)}</span>
                        </div>
                    ))}
                    {filteredUsers.length === 0 && <p style={muted}>No users.</p>}
                </div>}

            {!loading && !error && mode === 'games' &&
                <div className={'layout vertical'} style={{minHeight: 0, flexGrow: 1, overflowY: 'auto', gap: 2}}>
                    <div style={{display: 'grid', gridTemplateColumns: gamesGrid, gap: 8, padding: '4px 8px'}}>
                        <span style={headerCell}>Name</span>
                        <span style={headerCell}>Status</span>
                        <span style={headerCell}>Turn</span>
                        <span style={headerCell}>Players</span>
                        <span style={headerCell}>Created</span>
                    </div>
                    {pageGames.map(g => (
                        <div key={g.sessionId}
                             className={'box'}
                             style={{display: 'grid', gridTemplateColumns: gamesGrid, gap: 8, padding: '6px 8px', alignItems: 'center', color: 'white'}}>
                            <span>{g.orphaned && <span className={'warning-text'} title={'References a deleted/unknown player'}>! </span>}{g.name}</span>
                            <span style={muted}>{g.status}</span>
                            <span>{g.turn}</span>
                            <span style={muted}>{g.players}</span>
                            <span style={muted}>{formatDate(g.created)}</span>
                        </div>
                    ))}
                    {filteredGames.length === 0 && <p style={muted}>No games.</p>}
                </div>}

            {!loading && !error && total > 0 &&
                <div className={'layout horizontal center gap'} style={{justifyContent: 'center', paddingTop: 4}}>
                    <button disabled={safePage === 0} onClick={() => setPage(safePage - 1)}
                            style={{opacity: safePage === 0 ? 0.5 : 1}}>
                        <span className={'fa-solid fa-chevron-left'}/>
                    </button>
                    <span style={{color: 'var(--muted-text-color)', minWidth: 180, textAlign: 'center'}}>
                        Page {safePage + 1} / {totalPages} · {total} {mode === 'users' ? 'users' : 'games'}
                    </span>
                    <button disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}
                            style={{opacity: safePage >= totalPages - 1 ? 0.5 : 1}}>
                        <span className={'fa-solid fa-chevron-right'}/>
                    </button>
                </div>}

            {/* User detail */}
            {selected &&
                <div className={'layout vertical overlay'} onClick={() => setSelected(undefined)}>
                    <div className={'layout vertical gap box'} style={{padding: 20, gap: 12, width: 460, maxHeight: '80vh', overflowY: 'auto'}}
                         onClick={e => e.stopPropagation()}>
                        <div className={'layout horizontal center stretch'}>
                            {/* Prefer the detail's name: it's read from the profile row
                                and PlayFab's account info directly, where the list's
                                value can come from the segment index and lag. */}
                            <p className={'text header'}>{detail?.displayName || selected.displayName}</p>
                            <span className={'fa-solid fa-close'} style={{cursor: 'pointer', color: 'white'}} onClick={() => setSelected(undefined)}/>
                        </div>
                        <p style={mono}>{selected.id}</p>
                        {detailLoading && <p style={muted}>Loading…</p>}
                        {!detailLoading && detail &&
                            <div className={'layout vertical gap'}>
                                <Row label={'Display name'} value={detail.displayName || '(none)'}/>
                                {/* Says out loud when the list disagreed with the
                                    authoritative read — otherwise a name that is
                                    perfectly intact reads as lost. */}
                                {(detail.displayName || '') !== '' && detail.displayName !== selected.displayName &&
                                    <p style={{...muted, fontSize: 12, fontStyle: 'italic'}}>
                                        The list showed “{selected.displayName}” — its copy of the name is stale,
                                        not missing. This is the account's real display name.
                                    </p>}
                                <Row label={'PlayFab account'} value={detail.playFabAccount ? 'yes' : 'no'}/>
                                <Row label={'Email'} value={detail.email || '(none / not linked)'}/>
                                <Row label={'Stats'} value={`R:${detail.rating} W:${detail.wins} L:${losses(detail.wins, detail.matches)} M:${detail.matches}`}/>
                                <Row label={'Games'} value={`${detail.games.length}`}/>
                                <Row label={'Friends'} value={`${detail.friends.length}`}/>
                                <Row label={'Thumbnails'} value={`${detail.thumbnails} blob(s)`}/>
                                <div className={'layout horizontal center stretch gap'}
                                     style={{paddingTop: 10, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.12)'}}>
                                    <span style={{color: 'var(--muted-text-color)'}}>Editor role</span>
                                    <div className={'layout horizontal center gap'}>
                                        {/* Editing roles is SysAdmin-only. Admins can open
                                            the Users tab and read roles, but the control is
                                            disabled for them (backend enforces the same). */}
                                        <select value={role} onChange={e => setRole(e.target.value)}
                                                disabled={applying || !canEditRoles || !detail.playFabAccount}>
                                            {EDITOR_ROLES.map(r => (
                                                <option key={r} value={r}>{EDITOR_ROLE_LABELS[r]}</option>
                                            ))}
                                        </select>
                                        {!detail.playFabAccount
                                            ? <span style={{...muted, fontSize: 12}}
                                                    title={'Editor roles live on the PlayFab account; this user has none, so a role can\'t be assigned.'}>
                                                no PlayFab account
                                            </span>
                                            : canEditRoles
                                                ? <button onClick={applyRole}
                                                          disabled={applying || role === (detail.editorRole || 'none')}>
                                                    {applying ? '…' : 'Apply'}
                                                </button>
                                                : <span style={{...muted, fontSize: 12}} title={'Only SysAdmin can change roles'}>
                                                    SysAdmin only
                                                </span>}
                                    </div>
                                </div>
                            </div>}
                        {!detailLoading && !detail && <p className={'warning-text'}>Could not load details.</p>}
                    </div>
                </div>}
        </div>
    );
}

function Row(props: {label: string; value: string}) {
    return (
        <div className={'layout horizontal stretch gap'}>
            <span style={{color: 'var(--muted-text-color)'}}>{props.label}</span>
            <span style={{textAlign: 'right', color: 'white'}}>{props.value}</span>
        </div>
    );
}

// Renders the editor-role cell: the role's label (admin/sysadmin emphasized),
// or a muted dash when the user has no role.
function roleCell(role?: string) {
    if (!role || role === 'none') return <span style={{color: 'var(--muted-text-color)'}}>—</span>;
    const label = EDITOR_ROLE_LABELS[role as EditorRole] ?? role;
    const strong = role === 'admin' || role === 'sysadmin';
    return <span style={{color: strong ? 'var(--tint-color)' : undefined, fontWeight: strong ? 600 : undefined}}>{label}</span>;
}

// Small inline tag flagging accounts that aren't in both stores. NO PLAYFAB
// (profile row, no PlayFab account) can't hold an editor role → red; NO PROFILE
// (PlayFab account, no game profile yet) is informational → amber. "OK"/unknown
// renders nothing.
function statusTag(status?: string) {
    if (!status || status === 'OK') return null;
    const color = status === 'NO PLAYFAB' ? '#ff6666' : '#e0b000';
    return (
        <span style={{marginLeft: 6, fontSize: 10, fontWeight: 600, color,
                      border: `1px solid ${color}`, borderRadius: 4, padding: '1px 4px', verticalAlign: 'middle'}}>
            {status}
        </span>
    );
}

function formatDate(s?: string): string {
    if (!s) return '(never)';
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toISOString().slice(0, 16).replace('T', ' ');
}

export default UsersEditorView;
